// 2026-07-02: the post-ATC "complete the set" card.
//
// The moment right after add-to-cart is the point of maximum intent, and it
// used to be a dead end ("Start a new design below"). This card offers the
// top auto-paired bundle member with the customer's design already applied
// (same _rk_group_id + propagated design properties, so production resolves
// the same BuilderDesign), sold on the storewide value unlocks rather than
// a discount: adding a second piece typically crosses the $90 free-shipping
// and/or $250 free-socks thresholds.
//
// Variant handling: single-variant members (or ones the customer already
// picked a variant for in the bundle section) are true one-tap. Multi-variant
// members expose the same AxisPickerBody the bundle rows use.

import type { BundleMember, Config } from "../types";
import type { ProductJsonShape } from "../api";
import { AxisPickerBody } from "./BundlePreviewSection";

function moneyFmt(cents: number): string {
  const d = cents / 100;
  return Number.isInteger(d) ? `$${d}` : `$${d.toFixed(2)}`;
}

export function SecondItemCard({
  member,
  productData,
  fallbackPriceCents,
  discountPct,
  thresholds,
  cartCents,
  pickedVariantId,
  onPickVariant,
  busy,
  justAdded,
  onAdd,
}: {
  member: BundleMember;
  productData: ProductJsonShape | null;
  /** Price used when no variant pick narrows it down. */
  fallbackPriceCents: number | null;
  /** Multi-buy tier % this member would earn at checkout (0 = none). */
  discountPct: number;
  thresholds: Config["thresholds"];
  /** Current cart subtotal in cents (already includes the just-added item). */
  cartCents: number;
  pickedVariantId: string | null;
  onPickVariant: (memberId: string, variantId: string) => void;
  busy: boolean;
  justAdded: boolean;
  onAdd: () => void;
}) {
  const variants = productData?.variants ?? [];
  const picked = pickedVariantId
    ? variants.find((v) => String(v.id) === pickedVariantId)
    : null;
  const needsPick = variants.length > 1 && !picked;
  const priceCents =
    (picked && typeof picked.price === "number" ? picked.price : null) ??
    fallbackPriceCents;

  const title = productData?.title || member.productTitle || member.productHandle;
  const img = productData?.featured_image
    ? `${productData.featured_image}${productData.featured_image.includes("?") ? "&" : "?"}width=160`
    : null;

  // Which storewide unlocks would ADDING THIS PIECE cross?
  const ths = (thresholds ?? [])
    .slice()
    .sort((a, b) => a.amountCents - b.amountCents);
  const crossed =
    priceCents != null
      ? ths.filter(
          (t) => cartCents < t.amountCents && cartCents + priceCents >= t.amountCents,
        )
      : [];
  const nextAfter =
    priceCents != null
      ? ths.find((t) => cartCents + priceCents < t.amountCents)
      : null;

  let valueLine: string | null = null;
  if (crossed.length > 0) {
    valueLine = `Adds ${crossed.map((t) => t.label).join(" + ")}`;
  } else if (nextAfter && priceCents != null) {
    valueLine = `Gets you ${moneyFmt(nextAfter.amountCents - (cartCents + priceCents))} from ${nextAfter.label}`;
  }

  return (
    <div className="rk-pc-second-item">
      <div className="rk-pc-second-item__head">Complete the set</div>
      <div className="rk-pc-second-item__row">
        {img && (
          <span className="rk-pc-second-item__thumb" aria-hidden>
            <img src={img} alt="" loading="lazy" />
          </span>
        )}
        <div className="rk-pc-second-item__meta">
          <div className="rk-pc-second-item__title">{title}</div>
          <div className="rk-pc-second-item__sub">
            {member.propagateDesign
              ? "Same design, stitched on this one too"
              : "The matching plain piece"}
          </div>
          <div className="rk-pc-second-item__price">
            {priceCents != null ? moneyFmt(priceCents) : ""}
            {discountPct > 0 && (
              <span className="rk-pc-second-item__disc">
                −{discountPct}% at checkout
              </span>
            )}
          </div>
        </div>
      </div>
      {valueLine && (
        <div className="rk-pc-second-item__value">{valueLine}</div>
      )}
      {needsPick && productData && (
        <div className="rk-pc-second-item__picker">
          <AxisPickerBody
            memberId={member.id}
            productData={productData}
            pickedVariantId={pickedVariantId}
            onPickVariant={onPickVariant}
          />
        </div>
      )}
      <button
        type="button"
        className="rk-pc-second-item__cta"
        disabled={busy || justAdded || needsPick || priceCents == null}
        onClick={onAdd}
      >
        {justAdded
          ? "Added to cart ✓"
          : busy
            ? "Adding…"
            : needsPick
              ? "Pick a colour and size first"
              : "Add it to my order"}
      </button>
    </div>
  );
}
